import { formatNumber } from '@captaincause/lib/format'
import {
  actionDescription,
  floorImpactItems,
} from '@captaincause/lib/impact/computeImpact'
import { Icon } from '../Icon'
import { tm } from '../tm'
import { Container } from './Container'
import { ProjectImpactConversion } from './Conversion'
import i18n from './i18n'
import type { Locales, Project } from '@captaincause/types'

interface ProjectImpactCumulatedProps {
  project: Project
  locale: Locales
  showOrganizationLogo?: boolean
  className?: string
}

export function ProjectImpactCumulated({
  project,
  locale,
  showOrganizationLogo = true,
  className = '',
}: ProjectImpactCumulatedProps) {
  const t = i18n[locale]

  if (!project.action || !('cumulated_num_items' in project.action)) {
    return (
      <Container
        project={project}
        locale={locale}
        showOrganizationLogo={showOrganizationLogo}
        hideImpactBox
        className={className}
      />
    )
  }

  if (!project.contributors_count) {
    return (
      <Container
        project={project}
        locale={locale}
        showOrganizationLogo={showOrganizationLogo}
        className={className}
      >
        <ProjectImpactConversion
          project={project}
          locale={locale}
          className="text-dark-beige"
        />
        <div className="flex flex-col gap-x-3 text-lg font-medium text-black">
          {t.noContribution}
        </div>
      </Container>
    )
  }

  if (!project.action.cumulated_num_items) {
    return (
      <Container
        project={project}
        locale={locale}
        showOrganizationLogo={showOrganizationLogo}
        className={className}
      >
        <ProjectImpactConversion
          project={project}
          locale={locale}
          className="text-md font-medium text-black"
        />
      </Container>
    )
  }

  const items = floorImpactItems(project.action.cumulated_num_items)

  const percentIncrease =
    project.donation_increase_percent &&
    project.donation_increase_percent > 1 &&
    project.donation_increase_percent < 10000
      ? project.donation_increase_percent
      : undefined

  return (
    <Container
      project={project}
      locale={locale}
      className={className}
      showOrganizationLogo={showOrganizationLogo}
    >
      <div
        className={tm(
          'text-xs text-dark-beige',
          project.is_done && 'text-dark-green',
        )}
      >
        {project.is_done ? t.thanksToGenerosity : t.thanksToYou}
      </div>
      <div className="flex flex-col gap-x-3 text-black">
        <div className="flex items-center gap-x-2">
          <div className="text-3xl font-extrabold">
            {formatNumber(items, locale)}{' '}
          </div>
          {percentIncrease ? (
            <span className="rounded-xl bg-medium-green px-1 py-0.5 text-[12px] font-normal tracking-normal text-dark-green">
              <Icon
                icon="arrow-increase"
                className="mr-1"
                inline
                size="mini"
                fill="fill-dark-green"
              />
              {formatNumber(percentIncrease, locale)}%
            </span>
          ) : null}
        </div>

        <div className="line-clamp-2 text-sm font-medium">
          {actionDescription({
            action: project.action,
            numItems: project.action.cumulated_num_items,
            length: 'long',
          })}
        </div>
      </div>
    </Container>
  )
}
